import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconMapPin } from '../../../icons';

interface AddressData {
  street: string;
  zip: string;
  city: string;
  country: string;
}

interface AddressStepProps {
  initialData: AddressData;
  onSave: (data: AddressData) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
}

const COUNTRY_OPTIONS = [
  { code: 'DE', name: 'Deutschland' },
  { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' },
  { code: 'NL', name: 'Niederlande' },
  { code: 'BE', name: 'Belgien' },
  { code: 'LU', name: 'Luxemburg' },
  { code: 'FR', name: 'Frankreich' },
  { code: 'IT', name: 'Italien' },
  { code: 'ES', name: 'Spanien' },
  { code: 'PL', name: 'Polen' },
  { code: 'CZ', name: 'Tschechien' },
  { code: 'GB', name: 'Großbritannien' },
  { code: 'US', name: 'USA' },
];

const AddressStep: React.FC<AddressStepProps> = ({
  initialData,
  onSave,
  onNext,
  onBack,
}) => {
  const { t } = useTranslation('onboarding');
  const [formData, setFormData] = useState<AddressData>({
    street: initialData.street || '',
    zip: initialData.zip || '',
    city: initialData.city || '',
    country: initialData.country || 'DE',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<AddressData>>({});

  const validate = (): boolean => {
    const newErrors: Partial<AddressData> = {};
    
    if (!formData.street.trim()) {
      newErrors.street = t('steps.address.errorStreet');
    }
    if (!formData.zip.trim()) {
      newErrors.zip = t('steps.address.errorZip');
    }
    if (!formData.city.trim()) {
      newErrors.city = t('steps.address.errorCity');
    }
    if (!formData.country.trim()) {
      newErrors.country = t('steps.address.errorCountry');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (errors[name as keyof AddressData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;

    setIsSaving(true);

    try {
      await onSave({
        street: formData.street.trim(),
        zip: formData.zip.trim(),
        city: formData.city.trim(),
        country: formData.country.toUpperCase(),
      });
      onNext();
    } catch (err) {
      setErrors({ street: t('steps.address.errorSaving') });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <IconMapPin size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.address.title')}
        </h2>
        <p className="text-muted">
          {t('steps.address.description')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Street */}
        <div>
          <label htmlFor="street" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.address.street')} <span className="text-[var(--tone-danger-fg)]">*</span>
          </label>
          <input
            type="text"
            id="street"
            name="street"
            value={formData.street}
            onChange={handleChange}
            placeholder={t('steps.address.streetPlaceholder')}
            className={`w-full px-4 py-3 bg-surface-raised border ${errors.street ? 'border-red-500' : 'border-strong'} rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
            autoFocus
          />
          {errors.street && <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.street}</p>}
        </div>

        {/* ZIP and City Row */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label htmlFor="zip" className="block text-sm font-medium text-secondary mb-2">
              {t('steps.address.zip')} <span className="text-[var(--tone-danger-fg)]">*</span>
            </label>
            <input
              type="text"
              id="zip"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              placeholder="12345"
              className={`w-full px-4 py-3 bg-surface-raised border ${errors.zip ? 'border-red-500' : 'border-strong'} rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
            />
            {errors.zip && <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.zip}</p>}
          </div>

          <div className="col-span-2">
            <label htmlFor="city" className="block text-sm font-medium text-secondary mb-2">
              {t('steps.address.city')} <span className="text-[var(--tone-danger-fg)]">*</span>
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder={t('steps.address.cityPlaceholder')}
              className={`w-full px-4 py-3 bg-surface-raised border ${errors.city ? 'border-red-500' : 'border-strong'} rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all`}
            />
            {errors.city && <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.city}</p>}
          </div>
        </div>

        {/* Country */}
        <div>
          <label htmlFor="country" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.address.country')} <span className="text-[var(--tone-danger-fg)]">*</span>
          </label>
          <select
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            className={`w-full px-4 py-3 bg-surface-raised border ${errors.country ? 'border-red-500' : 'border-strong'} rounded-xl text-primary focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none cursor-pointer`}
          >
            {COUNTRY_OPTIONS.map(country => (
              <option key={country.code} value={country.code}>
                {country.name} ({country.code})
              </option>
            ))}
          </select>
          {errors.country && <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{errors.country}</p>}
        </div>

        {/* Info Box */}
        <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 border border-hairline">
          <p className="text-muted text-sm">
            💡 {t('steps.address.info')}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            {t('common.back')}
          </button>
          
          <button
            type="submit"
            disabled={isSaving}
            className="px-8 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-indigo-500/30"
          >
            {isSaving ? t('common.saving') : t('common.next')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressStep;



